import {
    Box,
    Button,
    capitalize,
    Checkbox,
    Chip,
    FormControlLabel,
    FormHelperText,
    IconButton,
    ListItemText,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    Switch,
    TextField as MuiTextField,
    Typography
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import {Field, FieldArray, useFormikContext} from "formik";
import {TextField} from 'formik-mui';
import GenericSelect from "../data/GenericSelect";
import {ChangeEvent, useMemo} from "react";
import {useMandantenHandling} from "../../hooks/mandanten/useMandantenHandling";
import {useSchedulersHandling} from "../../hooks/schedulers/useSchedulersHandling";
import type {SchedulerFormValues, SchedulerTimeSlotFormValues} from "../../types/schedulerTypes";
import {Mandant, Weekday} from "../../api";
import {schedulersStyles} from "../../styles/schedulers/schedulers.styles";

const WEEKDAYS: Weekday[] = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];
const WEEKDAY_SHORT_LABELS: Record<Weekday, string> = {
    MONDAY: "Mo",
    TUESDAY: "Tu",
    WEDNESDAY: "We",
    THURSDAY: "Th",
    FRIDAY: "Fr",
    SATURDAY: "Sa",
    SUNDAY: "Su",
};
const RESCHEDULING_STRATEGIES = ["CURRENT_SLOT", "NEXT_SLOT", "NEXT_DAY", "NEXT_WEEK"] as const;

interface TimeSlotGroup {
    start: string;
    end: string;
    weekdays: Weekday[];
    slotIndices: number[];
}

const byWeekdayOrder = (a: Weekday, b: Weekday) => WEEKDAYS.indexOf(a) - WEEKDAYS.indexOf(b);


function groupTimeSlots(timeSlots: SchedulerTimeSlotFormValues[]): TimeSlotGroup[] {
    const groups: TimeSlotGroup[] = [];
    timeSlots.forEach((slot, slotIndex) => {
        const group = groups.find((candidate) =>
            candidate.start === slot.start && candidate.end === slot.end && !candidate.weekdays.includes(slot.weekday)
        );
        if (group) {
            group.weekdays.push(slot.weekday);
            group.slotIndices.push(slotIndex);
        } else {
            groups.push({start: slot.start, end: slot.end, weekdays: [slot.weekday], slotIndices: [slotIndex]});
        }
    });
    return groups.map((group) => {
        const sorted = group.weekdays
            .map((weekday, position) => ({weekday, slotIndex: group.slotIndices[position]}))
            .sort((a, b) => byWeekdayOrder(a.weekday, b.weekday));
        return {
            ...group,
            weekdays: sorted.map((entry) => entry.weekday),
            slotIndices: sorted.map((entry) => entry.slotIndex),
        };
    });
}

interface FieldsContentSchedulerProps {
    canEditFields: boolean;
}

export default function FieldsContentScheduler({canEditFields}: FieldsContentSchedulerProps) {
    const {translation, scopeLabel} = useSchedulersHandling();
    const {loadMandanten} = useMandantenHandling();
    const {setFieldValue, setFieldTouched, values, errors, touched} = useFormikContext<SchedulerFormValues>();
    const timeSlotsError = typeof errors.timeSlots === 'string' ? errors.timeSlots : undefined;

    const scopeOptions = ["GLOBAL", "MANDANT", "AUFGABE"].map((id) => ({id, name: scopeLabel(id as any)}));
    const selectedScope = scopeOptions.find((option) => option.id === values.scope) ?? scopeOptions[0];

    const timeSlotGroups = useMemo(() => groupTimeSlots(values.timeSlots), [values.timeSlots]);

    const handleGroupWeekdaysChange = (groupIndex: number, newWeekdays: Weekday[]) => {
        const group = timeSlotGroups[groupIndex];
        if (!group) return;
        const uniqueNewWeekdays = Array.from(new Set(newWeekdays));
        if (uniqueNewWeekdays.length === 0) return;

        const groupSlotIndices = new Set(group.slotIndices);
        const existingByWeekday = new Map(
            group.slotIndices.map((slotIndex) => [values.timeSlots[slotIndex].weekday, values.timeSlots[slotIndex]] as const)
        );
        let nextSortOrder = Math.max(0, ...values.timeSlots.map((slot) => slot.sortOrder)) + 1;
        const groupSlots: SchedulerTimeSlotFormValues[] = uniqueNewWeekdays.map((weekday) =>
            existingByWeekday.get(weekday) ?? {weekday, start: group.start, end: group.end, sortOrder: nextSortOrder++}
        );

        const firstIndex = Math.min(...group.slotIndices);
        const nextSlots = values.timeSlots.filter((_slot, slotIndex) => !groupSlotIndices.has(slotIndex));
        const insertAt = values.timeSlots
            .slice(0, firstIndex)
            .filter((_slot, slotIndex) => !groupSlotIndices.has(slotIndex))
            .length;
        nextSlots.splice(insertAt, 0, ...groupSlots);

        setFieldValue('timeSlots', nextSlots);
    };

    const handleGroupTimeChange = (groupIndex: number, field: 'start' | 'end', value: string) => {
        const group = timeSlotGroups[groupIndex];
        if (!group) return;
        const groupSlotIndices = new Set(group.slotIndices);
        setFieldValue('timeSlots', values.timeSlots.map((slot, slotIndex) =>
            groupSlotIndices.has(slotIndex) ? {...slot, [field]: value} : slot
        ));
    };

    const handleRemoveGroup = (groupIndex: number) => {
        const group = timeSlotGroups[groupIndex];
        if (!group) return;
        const groupSlotIndices = new Set(group.slotIndices);
        setFieldValue('timeSlots', values.timeSlots.filter((_slot, slotIndex) => !groupSlotIndices.has(slotIndex)));
    };

    const handleAddGroup = () => {
        const nextSortOrder = Math.max(0, ...values.timeSlots.map((slot) => slot.sortOrder)) + 1;
        setFieldValue('timeSlots', [...values.timeSlots, {weekday: "MONDAY", start: "08:00:00", end: "17:00:00", sortOrder: nextSortOrder}]);
    };

    return (
        <Box id={'fields-content-scheduler-box'} display={"grid"} sx={schedulersStyles.fieldsContentScheduler.box}>
            <Field
                component={TextField}
                label={capitalize(translation('scheduler-attributes-name'))}
                name='name'
                required
                disabled={!canEditFields}
            />
            <Field
                component={GenericSelect}
                name={"scope"}
                label={capitalize(translation('scheduler-attributes-scope'))}
                options={scopeOptions}
                getOptionLabel={(option: { id: string, name: string }) => option.name}
                setSelected={(value: string) => setFieldValue("scope", value)}
                initialSelected={selectedScope}
                required
                disabled={!canEditFields}
            />
            {values.scope !== "GLOBAL" && (
                <Field
                    component={GenericSelect}
                    name={"mandantId"}
                    label={capitalize(translation('scheduler-attributes-mandant'))}
                    loadOptions={loadMandanten}
                    getOptionLabel={(mandant: Mandant) => mandant.name}
                    getSecondaryLabel={(mandant: Mandant) => mandant.id}
                    setSelected={(value: string) => setFieldValue("mandantId", value)}
                    sort={"name"}
                    required
                    disabled={!canEditFields}
                />
            )}

            <Box sx={schedulersStyles.fieldsContentScheduler.fullWidth}>
                <Typography variant="subtitle1" gutterBottom>
                    {capitalize(translation('scheduler-attributes-time-slots'))}
                </Typography>
                <Box display="flex" flexDirection="column" gap={1}>
                    {timeSlotGroups.map((group, groupIndex) => {
                        const groupErrors = errors.timeSlots?.[group.slotIndices[0]];
                        const groupTouched = touched.timeSlots?.[group.slotIndices[0]];
                        const endError = typeof groupErrors === 'object' && groupTouched?.end
                            ? groupErrors.end
                            : undefined;
                        return (
                            <Box key={`${group.start}-${group.end}-${groupIndex}`} sx={schedulersStyles.fieldsContentScheduler.timeSlotRow}>
                                <Select
                                    multiple
                                    size="small"
                                    value={group.weekdays}
                                    onChange={(e: SelectChangeEvent<Weekday[]>) => {
                                        const value = e.target.value;
                                        handleGroupWeekdaysChange(groupIndex, typeof value === "string" ? value.split(",") as Weekday[] : value);
                                    }}
                                    disabled={!canEditFields}
                                    sx={{minWidth: 220}}
                                    renderValue={(selected) => (
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                            {selected.map((day) => (
                                                <Chip key={day} label={WEEKDAY_SHORT_LABELS[day]} size="small"/>
                                            ))}
                                        </Stack>
                                    )}
                                >
                                    {WEEKDAYS.map((day) => (
                                        <MenuItem key={day} value={day}>
                                            <Checkbox checked={group.weekdays.includes(day)} size="small"/>
                                            <ListItemText primary={day}/>
                                        </MenuItem>
                                    ))}
                                </Select>
                                <MuiTextField
                                    type="time"
                                    size="small"
                                    value={group.start.slice(0, 5)}
                                    onChange={(e) => handleGroupTimeChange(groupIndex, 'start', `${e.target.value}:00`)}
                                    onBlur={() => setFieldTouched(`timeSlots.${group.slotIndices[0]}.start`, true)}
                                    disabled={!canEditFields}
                                    slotProps={{htmlInput: {step: 60}}}
                                />
                                <MuiTextField
                                    type="time"
                                    size="small"
                                    value={group.end.slice(0, 5)}
                                    onChange={(e) => handleGroupTimeChange(groupIndex, 'end', `${e.target.value}:00`)}
                                    onBlur={() => setFieldTouched(`timeSlots.${group.slotIndices[0]}.end`, true)}
                                    disabled={!canEditFields}
                                    slotProps={{htmlInput: {step: 60}}}
                                    error={Boolean(endError)}
                                    helperText={endError}
                                />
                                <IconButton size="small" color="error" disabled={!canEditFields} onClick={() => handleRemoveGroup(groupIndex)}>
                                    <DeleteOutlineIcon fontSize="small"/>
                                </IconButton>
                            </Box>
                        );
                    })}
                    <Button
                        startIcon={<AddIcon/>}
                        disabled={!canEditFields}
                        onClick={handleAddGroup}
                        sx={{alignSelf: 'flex-start'}}
                    >
                        {capitalize(translation('scheduler-attributes-time-slot-add'))}
                    </Button>
                    {timeSlotsError && (
                        <FormHelperText error>{capitalize(timeSlotsError)}</FormHelperText>
                    )}
                </Box>
            </Box>

            <Box sx={schedulersStyles.fieldsContentScheduler.fullWidth}>
                <Typography variant="subtitle1" gutterBottom>
                    {capitalize(translation('scheduler-attributes-rescheduling-strategies'))}
                </Typography>
                <FieldArray name="reschedulingStrategies">
                    {({push, remove}) => (
                        <Box display="flex" flexDirection="column" gap={1}>
                            {values.reschedulingStrategies.map((strategy, index) => {
                                const strategyErrors = errors.reschedulingStrategies?.[index];
                                const strategyTouched = touched.reschedulingStrategies?.[index];
                                const duplicateError = typeof strategyErrors === 'string' && strategyTouched
                                    ? strategyErrors
                                    : undefined;
                                return (
                                    <Box key={index} sx={schedulersStyles.fieldsContentScheduler.timeSlotRow}>
                                        {strategy.attemptNumber === 1 ? (
                                            <Typography variant="body2" sx={{width: 160, fontStyle: 'italic'}}>
                                                {capitalize(translation('scheduler-attributes-all-retries'))}
                                            </Typography>
                                        ) : (
                                            <MuiTextField
                                                type="number"
                                                size="small"
                                                label={capitalize(translation('scheduler-attributes-retry-from-attempt'))}
                                                value={strategy.attemptNumber}
                                                onChange={(e) => setFieldValue(`reschedulingStrategies.${index}.attemptNumber`, Number(e.target.value))}
                                                onBlur={() => setFieldTouched(`reschedulingStrategies.${index}`, true)}
                                                disabled={!canEditFields}
                                                slotProps={{htmlInput: {min: 2}}}
                                                sx={{width: 160}}
                                                error={Boolean(duplicateError)}
                                                helperText={duplicateError}
                                            />
                                        )}
                                        <MuiTextField
                                            select
                                            size="small"
                                            value={strategy.strategy}
                                            onChange={(e) => setFieldValue(`reschedulingStrategies.${index}.strategy`, e.target.value)}
                                            disabled={!canEditFields}
                                            sx={{minWidth: 160}}
                                        >
                                            {RESCHEDULING_STRATEGIES.map((s) => (
                                                <MenuItem key={s} value={s}>{s}</MenuItem>
                                            ))}
                                        </MuiTextField>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            disabled={!canEditFields || strategy.attemptNumber === 1}
                                            onClick={() => remove(index)}
                                        >
                                            <DeleteOutlineIcon fontSize="small"/>
                                        </IconButton>
                                    </Box>
                                );
                            })}
                            <Button
                                startIcon={<AddIcon/>}
                                disabled={!canEditFields}
                                onClick={() => push({
                                    attemptNumber: Math.max(1, ...values.reschedulingStrategies.map((s) => s.attemptNumber)) + 1,
                                    strategy: "NEXT_SLOT",
                                })}
                                sx={{alignSelf: 'flex-start'}}
                            >
                                {capitalize(translation('scheduler-attributes-rescheduling-strategy-add'))}
                            </Button>
                        </Box>
                    )}
                </FieldArray>
            </Box>

            <FormControlLabel
                control={
                    <Switch
                        checked={values.scheduleOnFreedays}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setFieldValue('scheduleOnFreedays', e.target.checked)}
                        disabled={!canEditFields}
                        color="primary"
                    />
                }
                label={<Typography variant="body1">{capitalize(translation('scheduler-attributes-schedule-on-freedays'))}</Typography>}
            />
            <FormControlLabel
                control={
                    <Switch
                        checked={values.scheduleOncePerTimeSlot}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setFieldValue('scheduleOncePerTimeSlot', e.target.checked)}
                        disabled={!canEditFields}
                        color="primary"
                    />
                }
                label={<Typography variant="body1">{capitalize(translation('scheduler-attributes-schedule-once-per-time-slot'))}</Typography>}
            />
            <FormControlLabel
                control={
                    <Switch
                        checked={values.callOutsideTimeSlots}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setFieldValue('callOutsideTimeSlots', e.target.checked)}
                        disabled={!canEditFields}
                        color="primary"
                    />
                }
                label={<Typography variant="body1">{capitalize(translation('scheduler-attributes-call-outside-time-slots'))}</Typography>}
            />
            <FormControlLabel
                control={
                    <Switch
                        checked={values.catchUpInNextWindow}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setFieldValue('catchUpInNextWindow', e.target.checked)}
                        disabled={!canEditFields}
                        color="primary"
                    />
                }
                label={<Typography variant="body1">{capitalize(translation('scheduler-attributes-catch-up-in-next-window'))}</Typography>}
            />
            <FormControlLabel
                control={
                    <Switch
                        checked={values.enableRandomDistribution}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setFieldValue('enableRandomDistribution', e.target.checked)}
                        disabled={!canEditFields}
                        color="primary"
                    />
                }
                label={<Typography variant="body1">{capitalize(translation('scheduler-attributes-enable-random-distribution'))}</Typography>}
            />
            <Field
                component={TextField}
                label={capitalize(translation('scheduler-attributes-max-concurrent-calls'))}
                name='maxConcurrentCalls'
                type='number'
                helperText={capitalize(translation('scheduler-attributes-max-concurrent-calls-hint'))}
                slotProps={{htmlInput: {min: 1, step: 1}}}
                disabled={!canEditFields}
            />

            <Box sx={schedulersStyles.fieldsContentScheduler.fullWidth}>
                <Typography variant="subtitle1" gutterBottom>
                    {capitalize(translation('scheduler-attributes-equidistant-scheduling'))}
                </Typography>
                <Box sx={schedulersStyles.fieldsContentScheduler.timeSlotRow}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={values.equidistantSchedulingEnabled}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setFieldValue('equidistantSchedulingEnabled', e.target.checked)}
                                disabled={!canEditFields}
                                color="primary"
                            />
                        }
                        label={<Typography variant="body1">{capitalize(translation('scheduler-attributes-equidistant-scheduling-enabled'))}</Typography>}
                    />
                    <Field
                        component={TextField}
                        label={capitalize(translation('scheduler-attributes-equidistant-scheduling-seconds'))}
                        name='equidistantSchedulingSeconds'
                        type='number'
                        size="small"
                        slotProps={{htmlInput: {min: 1, step: 1}}}
                        disabled={!canEditFields || !values.equidistantSchedulingEnabled}
                    />
                </Box>
            </Box>
        </Box>
    );
}
